import {headerConfig} from "./config";
import {getCssVariableValue} from "./scss_variables";
import {hasConsent} from "./utils";
import {initSiteBanner} from "./site-banner";

const SUPPORT_CHAT_MOBILE_QUERY = '(max-width: 767.98px)';
const SUPPORT_CHAT_IFRAME_SELECTOR = 'iframe[title="chat widget"]';
const SUPPORT_CHAT_OPEN_CLASS = 'support-chat-open';
const FUNCTIONAL_COOKIE_CATEGORY_PATTERN = /(functionality|functional|функцион|функціон|funkcjonal|funcional|funcțion|funcţion|funcionais|funcionale)/i;
const FUNCTIONAL_COOKIE_PREFERENCES_RETRY_LIMIT = 12;
const FUNCTIONAL_COOKIE_PREFERENCES_RETRY_DELAY = 150;
const COOKIE_PREFERENCES_ROOT_SELECTORS = [
    '#cc-main',
    '.cc-main',
    '.cc-window',
    '.freeprivacypolicy-com---preferences-center',
    '.freeprivacypolicy-com---pc',
    '[role="dialog"][aria-modal="true"]',
    '[class*="preferences"][role="dialog"]',
];

let supportChatInitialized = false;
let supportChatPendingOpen = false;
let supportChatPollInterval = null;
let supportChatPollAttempts = 0;
let supportChatSyncRaf = null;
let supportChatObserver = null;

function isSupportChatMobile() {
    return window.matchMedia(SUPPORT_CHAT_MOBILE_QUERY).matches;
}

function getSupportChatApi() {
    return window.Tawk_API || null;
}

function setSupportChatOpenState(isOpen) {
    document.body.classList.toggle(SUPPORT_CHAT_OPEN_CLASS, isOpen);
}

function updateSupportChatIframeClass() {
    const iframe = document.querySelector(SUPPORT_CHAT_IFRAME_SELECTOR);

    if (iframe) {
        iframe.classList.add('bottom-adjust');
    }

    return iframe;
}

function wrapSupportChatCallback(api, callbackName, handler) {
    const previousCallback = api[callbackName];

    api[callbackName] = function () {
        if (typeof previousCallback === 'function') {
            previousCallback.apply(this, arguments);
        }

        handler();
    };
}

function bindSupportChatCallbacks(api) {
    if (!api || api.__pulsGptSupportChatCallbacksBound) {
        return;
    }

    wrapSupportChatCallback(api, 'onLoad', function () {
        if (supportChatPendingOpen) {
            openSupportChat();
            return;
        }

        scheduleSupportChatSync();
    });

    wrapSupportChatCallback(api, 'onChatMaximized', function () {
        setSupportChatOpenState(true);
        scheduleSupportChatSync();
    });

    ['onChatMinimized', 'onChatHidden', 'onChatEnded'].forEach(function (callbackName) {
        wrapSupportChatCallback(api, callbackName, function () {
            setSupportChatOpenState(false);
            scheduleSupportChatSync();
        });
    });

    api.__pulsGptSupportChatCallbacksBound = true;
}

function stopSupportChatPoll() {
    if (supportChatPollInterval) {
        clearInterval(supportChatPollInterval);
        supportChatPollInterval = null;
    }
}

function startSupportChatPoll() {
    if (supportChatPollInterval) {
        return;
    }

    supportChatPollAttempts = 0;
    supportChatPollInterval = setInterval(function () {
        supportChatPollAttempts += 1;

        const hasApi = syncSupportChatVisibility();

        if (supportChatPendingOpen) {
            openSupportChat();
        }

        if ((hasApi && !supportChatPendingOpen) || supportChatPollAttempts >= 120) {
            stopSupportChatPoll();
        }
    }, 250);
}

function syncSupportChatVisibility() {
    updateSupportChatIframeClass();

    const api = getSupportChatApi();

    if (!api) {
        return false;
    }

    bindSupportChatCallbacks(api);

    if (supportChatPendingOpen) {
        openSupportChat();
        return true;
    }

    if (isSupportChatMobile()) {
        if (!document.body.classList.contains(SUPPORT_CHAT_OPEN_CLASS) && typeof api.hideWidget === 'function') {
            api.hideWidget();
        }
    } else {
        setSupportChatOpenState(false);

        if (typeof api.showWidget === 'function') {
            api.showWidget();
        }
    }

    return true;
}

function scheduleSupportChatSync() {
    if (supportChatSyncRaf) {
        return;
    }

    supportChatSyncRaf = window.requestAnimationFrame(function () {
        supportChatSyncRaf = null;
        syncSupportChatVisibility();
    });
}

function getSupportChatCookieModalElement() {
    return document.getElementById('supportChatCookieModal');
}

function showSupportChatCookieNotice() {
    const modalElement = getSupportChatCookieModalElement();

    if (!modalElement || typeof bootstrap === 'undefined') {
        return;
    }

    const fallback = modalElement.querySelector('.support-chat-cookie-fallback');

    if (fallback) {
        fallback.hidden = true;
    }

    const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
    modal.show();
}

function isVisibleElement(element) {
    if (!element) {
        return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
}

function getCookiePreferencesRoots() {
    const roots = COOKIE_PREFERENCES_ROOT_SELECTORS.flatMap(function (selector) {
        return Array.from(document.querySelectorAll(selector));
    }).filter(isVisibleElement);

    if (roots.length) {
        return roots;
    }

    return [document.body];
}

function getFunctionalCookieSearchText(element) {
    return [
        element.id,
        element.className,
        element.getAttribute('name'),
        element.getAttribute('value'),
        element.getAttribute('aria-label'),
        element.getAttribute('aria-controls'),
        element.getAttribute('data-cookie-consent'),
        element.getAttribute('data-cookie-consent-level'),
        element.getAttribute('data-consent-level'),
        element.getAttribute('data-category'),
        element.getAttribute('data-id'),
        element.textContent,
    ].filter(Boolean).join(' ');
}

function isFunctionalCookieCandidate(element) {
    return FUNCTIONAL_COOKIE_CATEGORY_PATTERN.test(getFunctionalCookieSearchText(element));
}

function isSafeFunctionalCookieControl(element) {
    const tagName = element.tagName.toLowerCase();
    const role = (element.getAttribute('role') || '').toLowerCase();
    const className = String(element.className).toLowerCase();

    if (['input', 'select', 'textarea', 'label'].includes(tagName)) {
        return false;
    }

    if (['checkbox', 'switch', 'radio'].includes(role)) {
        return false;
    }

    if (element.hasAttribute('aria-checked') || element.hasAttribute('aria-pressed')) {
        return false;
    }

    if (/(switch|toggle|checkbox|slider)/.test(className)) {
        return false;
    }

    return tagName === 'button'
        || tagName === 'a'
        || tagName === 'summary'
        || role === 'tab'
        || role === 'button';
}

function findFunctionalCookieControl(root) {
    const controls = Array.from(root.querySelectorAll([
        'button',
        'a',
        'summary',
        '[role="tab"]',
        '[role="button"]',
        '[data-cookie-consent-level="functionality"]',
        '[data-consent-level="functionality"]',
        '[data-category="functionality"]',
    ].join(',')));

    return controls.find(function (control) {
        return isVisibleElement(control)
            && isSafeFunctionalCookieControl(control)
            && isFunctionalCookieCandidate(control);
    });
}

function findFunctionalCookieSection(root) {
    const sections = Array.from(root.querySelectorAll([
        '[id*="functionality" i]',
        '[class*="functionality" i]',
        '[data-cookie-consent-level="functionality"]',
        '[data-consent-level="functionality"]',
        '[data-category="functionality"]',
        'section',
        'article',
        'fieldset',
        '[role="tabpanel"]',
        '[role="group"]',
    ].join(',')));

    return sections.find(function (section) {
        return isVisibleElement(section) && isFunctionalCookieCandidate(section);
    });
}

function focusFunctionalCookiePreferences() {
    const roots = getCookiePreferencesRoots();

    for (const root of roots) {
        const control = findFunctionalCookieControl(root);

        if (control) {
            control.click();
            control.focus({preventScroll: true});
        }

        const section = findFunctionalCookieSection(root);

        if (section) {
            section.scrollIntoView({block: 'center', behavior: 'smooth'});

            if (typeof section.focus === 'function') {
                section.focus({preventScroll: true});
            }

            return true;
        }

        if (control) {
            return true;
        }
    }

    return false;
}

function scheduleFunctionalCookiePreferencesFocus(attempt = 0) {
    window.setTimeout(function () {
        window.requestAnimationFrame(function () {
            if (focusFunctionalCookiePreferences() || attempt >= FUNCTIONAL_COOKIE_PREFERENCES_RETRY_LIMIT) {
                return;
            }

            scheduleFunctionalCookiePreferencesFocus(attempt + 1);
        });
    }, attempt === 0 ? 50 : FUNCTIONAL_COOKIE_PREFERENCES_RETRY_DELAY);
}

function openSupportChatCookieSettings() {
    const preferencesLink = document.getElementById('open_preferences_center');
    const modalElement = getSupportChatCookieModalElement();

    if (preferencesLink) {
        if (modalElement && typeof bootstrap !== 'undefined') {
            const modal = bootstrap.Modal.getInstance(modalElement);

            if (modal) {
                modal.hide();
            }
        }

        preferencesLink.click();
        scheduleFunctionalCookiePreferencesFocus();
        return;
    }

    if (modalElement) {
        const fallback = modalElement.querySelector('.support-chat-cookie-fallback');

        if (fallback) {
            fallback.hidden = false;
        }
    }
}

function openSupportChat() {
    updateSupportChatIframeClass();

    const api = getSupportChatApi();

    if (!api || typeof api.maximize !== 'function') {
        if (hasConsent(['functionality'])) {
            supportChatPendingOpen = true;
            startSupportChatPoll();
            return false;
        }

        supportChatPendingOpen = false;
        showSupportChatCookieNotice();
        return false;
    }

    bindSupportChatCallbacks(api);
    supportChatPendingOpen = false;
    setSupportChatOpenState(true);

    if (typeof api.showWidget === 'function') {
        api.showWidget();
    }

    window.requestAnimationFrame(function () {
        api.maximize();
    });
    setTimeout(function () {
        api.maximize();
    }, 150);

    return true;
}

function initSupportChatController() {
    if (supportChatInitialized) {
        return;
    }

    supportChatInitialized = true;
    window.PulsGptSupportChat = {
        open: openSupportChat,
        syncVisibility: scheduleSupportChatSync,
    };

    window.addEventListener('resize', scheduleSupportChatSync);
    supportChatObserver = new MutationObserver(scheduleSupportChatSync);
    supportChatObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
    });

    scheduleSupportChatSync();
    startSupportChatPoll();
}


function hideNavContainer(classNameA, classNameB) {
    const color = getCssVariableValue('--color-content-link');

    $(classNameA).css('transform', 'scaleY(0)') // '.profile-dropdown-container'
    $(classNameA).css('top', '10px')
    $(classNameB).css('color', color)
}

function showNavContainer(classNameA, classNameB) {
    const color = getCssVariableValue('--color-content-link-hover');

    $(classNameA).css('transform', 'scaleY(1)') //
    $(classNameA).css('top', '20px')
    $(classNameB).css('color', color)
}


function bindHeaderEvents() {
    let lastScrollTop = 0;
    const body = $('body');

    // Listen to the scroll event
    // Showing the header clears its inline top: the stylesheet puts it under the site banner while that is in view.
    $(window).scroll(function () {
        // Get current scroll top position
        let currentScrollTop = $(this).scrollTop();

        if (currentScrollTop > lastScrollTop) {
            // Scrolling down; the header stays while the notification list is open.
            if (!document.body.classList.contains('inapp-bell-open')) {
                $('.app-header:not(.app-header-absolute):not(.app-header-sticky)').css('top', '-72px')
            }
        } else if (currentScrollTop < lastScrollTop) {
            $('.app-header:not(.app-header-absolute):not(.app-header-sticky)').css('top', '')
        }

        lastScrollTop = currentScrollTop;


    });


    body.on("mouseover", '.header-profile-nav-container', function (event) {
        clearTimeout(headerConfig.navMenuTimeout)
        showNavContainer('.profile-dropdown-container', '.profile-nav-link')

    })


    body.on("mouseover", '.profile-dropdown-container', function (event) {
        clearTimeout(headerConfig.navMenuTimeout)
        showNavContainer('.profile-dropdown-container', '.profile-nav-link')

    })


    body.on("mouseout", '.header-profile-nav-container', function (event) {
        headerConfig.navMenuTimeout = setTimeout(() => hideNavContainer('.profile-dropdown-container', '.profile-nav-link'), 1000)

    })
    body.on("mouseout", '.profile-dropdown-container', function (event) {
        headerConfig.navMenuTimeout = setTimeout(() => hideNavContainer('.profile-dropdown-container', '.profile-nav-link'), 1000)

    })

    body.on("mouseover", '.language-nav-container,.language-dropdown-container', function (event) {

        clearTimeout(headerConfig.langMenuTimeout)
        showNavContainer('.language-dropdown-container', '.language-nav-text')

    })


    body.on("mouseout", '.language-nav-container', function (event) {
        headerConfig.langMenuTimeout = setTimeout(() => hideNavContainer('.language-dropdown-container', '.language-nav-text'), 1000)

    })
    body.on("mouseout", '.language-dropdown-container', function (event) {
        headerConfig.langMenuTimeout = setTimeout(() => hideNavContainer('.language-dropdown-container', '.language-nav-text'), 1000)

    })

    body.on("click", '.nav_burger_container', function (event) {
        const mobileMenu = $('.profile-mobile-container')
        // The menu opens at its top, not where it was scrolled to; reset before it slides in, not while it slides out.
        if (!mobileMenu.hasClass('active')) {
            mobileMenu.scrollTop(0)
        }
        $('.nav_burger, .profile-mobile-container').toggleClass('active')
    })

    body.on("click", '.js-open-support-chat', function (event) {
        event.preventDefault();
        $('.nav_burger, .profile-mobile-container').removeClass('active')
        openSupportChat();
    })

    body.on("click", '.js-open-support-chat-cookie-settings', function (event) {
        event.preventDefault();
        openSupportChatCookieSettings();
    })
}

// The header, site banner and support chat that every visitor gets, signed in or not. The common bundle runs it on
// every page; the landing bundle runs it too, because guests on the home page do not load the common bundle. The
// two bundles have separate webpack runtimes, so the guard lives on window: a signed-in visitor of the home page
// loads both and must not bind the burger (a toggle) or the menus twice.
export function initPublicShell() {
    if (window.__pulsPublicShellReady) {
        return;
    }
    window.__pulsPublicShellReady = true;
    initSiteBanner();
    initSupportChatController();
    bindHeaderEvents();
}
